import {
  ErrorLogAnalysisModel, ErrorLogAnalysisRequestModel, ErrorLogStatus, ErrorLogStatusList
} from '../../../business-entities/error-log-analysis-report.model';

// The API returns UTC; values without a 'Z' suffix are UTC as well.
export function toLocalDate(value: string): Date {
  return value ? new Date(value.endsWith('Z') ? value : value + 'Z') : null;
}

// Local time as "2026-10-21, 4:57 PM".
function formatStatusDateTime(value: string): string {
  let date = toLocalDate(value);
  if (!date) {
    return '';
  }
  let pad = (n: number) => (n < 10 ? '0' + n : '' + n);
  let hours = date.getHours() % 12 || 12;
  let period = date.getHours() >= 12 ? 'PM' : 'AM';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}, ${hours}:${pad(date.getMinutes())} ${period}`;
}

// "User - 2026-10-21, 4:57 PM", empty when the status was never changed.
export function getStatusUpdatedText(item: Pick<ErrorLogAnalysisModel, 'statusUpdatedBy' | 'statusUpdatedOn'>): string {
  return [item.statusUpdatedBy, formatStatusDateTime(item.statusUpdatedOn)].filter((value) => value).join(' - ');
}

// Start of the first day (local) to the start of the day after the last day (local), sent as UTC because ErrorLog stores UTC.
export function getReportRange(dateRange: Date[]): { fromDate: string, toDate: string } {
  let from = new Date(dateRange[0].getFullYear(), dateRange[0].getMonth(), dateRange[0].getDate());
  let to = new Date(dateRange[1].getFullYear(), dateRange[1].getMonth(), dateRange[1].getDate() + 1);
  return { fromDate: from.toISOString(), toDate: to.toISOString() };
}

export function getAnalysisRequest(dateRange: Date[]): ErrorLogAnalysisRequestModel {
  return getReportRange(dateRange);
}

export function getWindowRange(windowStart: string): { fromDate: string, toDate: string } {
  let start = toLocalDate(windowStart);
  return { fromDate: start.toISOString(), toDate: new Date(start.getTime() + 60 * 1000).toISOString() };
}

export function getStatusOption(status: ErrorLogStatus) {
  return ErrorLogStatusList.find((item) => item.value === status) || ErrorLogStatusList[0];
}

// Rule text ends with "Thrown at: ..." - the part before it is the readable cause.
export function getCauseText(cause: string): string {
  return cause ? cause.split(' Thrown at:')[0] : '';
}

export function getThrownAt(cause: string): string {
  let parts = cause ? cause.split(' Thrown at:') : [];
  return parts.length > 1 ? parts[1].trim() : '';
}

// Copies text to the clipboard; falls back to a hidden textarea where the Clipboard API is not available (non-HTTPS, old browsers).
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    let area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch {
    return false;
  }
}

export function formatJson(value: string): string {
  if (!value) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}
